import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, Sparkles, X, FileText, BarChart3, Newspaper, MessageSquareQuote, ArrowUp } from 'lucide-react';
import { Avatar } from './ui/avatar';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';
const suggestions = [{
  icon: FileText,
  title: 'Genera un report',
  description: 'Crea un report mensile per il cliente con i dati più recenti'
}, {
  icon: BarChart3,
  title: 'Analizza le statistiche',
  description: 'Mostrami un riepilogo delle performance di copertura mediatica'
}, {
  icon: Newspaper,
  title: 'Riassumi la rassegna',
  description: 'Genera un executive summary della rassegna di oggi'
}, {
  icon: MessageSquareQuote,
  title: 'Confronta i periodi',
  description: 'Confronta la copertura di questo mese con il mese precedente'
}];
type Message = {
  id: string;
  role: 'user' | 'ai';
  content: string;
  isTyping?: boolean;
};
function getSimulatedResponse(input: string): string {
  const lowerInput = input.toLowerCase();
  if (lowerInput.includes('report') || lowerInput.includes('genera')) {
    return 'Ho generato il report mensile per Intrum relativo al periodo 1-20 Aprile 2026. Ecco un riepilogo:\n\n**20 articoli** analizzati su 12 testate\n**Score medio: 7.4** (+12% vs mese precedente)\n**AVE totale: €187k**\n**Sentiment: 65% positivo**, 25% neutro, 10% negativo\n\nI temi principali riguardano la gestione dei crediti deteriorati (NPL), le operazioni di cartolarizzazione e il ruolo di Intrum nel mercato italiano del credit management. Il report è pronto per il download nella sezione Report.';
  }
  if (lowerInput.includes('statistiche') || lowerInput.includes('analizza') || lowerInput.includes('performance')) {
    return 'Ecco il riepilogo delle performance di copertura mediatica per Intrum:\n\n**Copertura**: 20 articoli su 12 testate uniche, con 5 uscite su testate Tier 1 (Il Sole 24 Ore, MF Milano Finanza, Corriere della Sera)\n**Autori**: 14 giornalisti unici hanno coperto Intrum, con Luca Davi (Il Sole 24 Ore) come autore più attivo\n**Posizionamento**: 4 articoli in prima pagina, 8 con menzione nel titolo\n**Score medio**: 7.4/10, in crescita rispetto al periodo precedente\n\nLa visibilità è particolarmente forte nel segmento finanziario specializzato.';
  }
  if (lowerInput.includes('riassumi') || lowerInput.includes('rassegna') || lowerInput.includes('summary') || lowerInput.includes('executive')) {
    return "Ecco l'executive summary della rassegna di oggi:\n\n**Intrum nella stampa italiana — Aprile 2026**\n\nLa copertura mediatica di Intrum nel periodo analizzato è dominata da tre filoni principali:\n\n1. **Operazioni NPL**: L'acquisizione del portafoglio da €1.2 miliardi da UniCredit ha generato ampia copertura su Il Sole 24 Ore e MF Milano Finanza, con tono prevalentemente positivo.\n\n2. **Strategia italiana**: Diverse testate hanno approfondito il piano di espansione di Intrum in Italia, con interviste al management e analisi del posizionamento competitivo.\n\n3. **Mercato del credito**: Intrum è citata come player di riferimento nelle analisi di settore sul mercato NPL italiano, consolidando il posizionamento come leader.\n\nIl sentiment complessivo è positivo (65%), con un AVE totale di €187k.";
  }
  if (lowerInput.includes('confronta') || lowerInput.includes('confronto') || lowerInput.includes('periodo') || lowerInput.includes('mese')) {
    return "Ecco il confronto tra la copertura di questo mese e il mese precedente:\n\nArticoli: 20 vs 16 (+25%)\nScore medio: 7.4 vs 6.8 (+8.8%)\nAVE totale: €187k vs €142k (+31.7%)\nTier 1: 5 vs 3 (+66.7%)\nSentiment positivo: 65% vs 56% (+9pp)\n\n**Trend positivo**: La copertura è in netta crescita sia in volume che in qualità. L'aumento delle uscite Tier 1 (+66.7%) è particolarmente significativo e riflette il maggiore interesse delle testate di riferimento per le operazioni di Intrum.";
  }
  return 'Ho analizzato la tua richiesta. Sulla base dei dati disponibili per Intrum, posso dirti che la copertura mediatica nel periodo corrente mostra un trend positivo con 20 articoli pubblicati su 12 testate. Lo score medio di rilevanza è 7.4/10 e il sentiment è prevalentemente positivo (65%). Vuoi che approfondisca qualche aspetto specifico?';
}
function formatText(text: string) {
  return text.split('\n').map((line, i) => {
    if (line === '') return <br key={i} data-id="element-119" />;
    const parts = line.split(/(\*\*.*?\*\*)/g);
    return <div key={i} className="min-h-[1.5em]" data-id="element-120">
        {parts.map((part, j) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={j} className="font-semibold text-foreground" data-id="element-121">
                {part.slice(2, -2)}
              </strong>;
        }
        return <span key={j} data-id="element-122">{part}</span>;
      })}
      </div>;
  });
}
function TypewriterText({
  text,
  onComplete
}: {
  text: string;
  onComplete: () => void;
}) {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace
  useEffect(() => {
    if (currentIndex < words.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(prev => prev + words[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 40); // ~40ms per word/token
      return () => clearTimeout(timeout);
    } else {
      onComplete();
    }
  }, [currentIndex, words, onComplete]);
  return <>{formatText(displayedText)}</>;
}
function AIModeOverlay({
  onClose
}: {
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<'welcome' | 'chat'>('welcome');
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isAiTyping, setIsAiTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isAiTyping]);
  const handleSubmit = (text: string) => {
    if (!text.trim() || isAiTyping) return;
    if (phase === 'welcome') {
      setPhase('chat');
    }
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text.trim()
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsAiTyping(true);
    // Simulate network delay before starting to type
    setTimeout(() => {
      const aiResponse = getSimulatedResponse(text);
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'ai',
        content: aiResponse,
        isTyping: true
      };
      setMessages(prev => [...prev, aiMsg]);
    }, 600);
  };
  const handleAiComplete = (id: string) => {
    setMessages(prev => prev.map(m => m.id === id ? {
      ...m,
      isTyping: false
    } : m));
    setIsAiTyping(false);
  };
  return <motion.div key="ai-overlay" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3,
    ease: 'easeInOut'
  }} className="fixed inset-0 bg-white flex flex-col" style={{
    zIndex: 99999
  }} data-id="element-123">
      {/* Top bar */}
      <div className="h-16 border-b border-border flex items-center justify-between px-6 flex-shrink-0 bg-white" data-id="element-124">
        <button type="button" onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }} className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:bg-muted transition-colors bg-transparent border-none outline-none" style={{
        zIndex: 100000
      }} aria-label="Chiudi AI Mode" data-id="element-125">
          <X className="h-5 w-5 text-foreground pointer-events-none" data-id="element-126" />
        </button>

        <div className="flex items-center gap-3" data-id="element-127">
          <span className="font-medium text-sm text-foreground" data-id="element-128">AI Mode</span>
        </div>

        <div className="flex items-center gap-2" data-id="element-129">
          <span className="text-sm text-muted-foreground mr-1" data-id="element-130">Marco C.</span>
          <Avatar className="h-9 w-9" data-id="element-131">
            <img src="https://cdn.magicpatterns.com/uploads/gMQpnDVF4sUMZFXw5c1pgV/kh4ijivu_400x400.jpg" alt="Marco C." className="w-full h-full object-cover rounded-full" data-id="element-132" />
          </Avatar>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden relative flex flex-col" data-id="element-133">
        <AnimatePresence mode="wait" data-id="element-134">
          {phase === 'welcome' ? <motion.div key="welcome-phase" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="flex-1 flex flex-col items-center justify-center px-6 -mt-16 overflow-y-auto" data-id="element-135">
              <div className="w-full max-w-2xl space-y-3 mb-10" data-id="element-136">
                <div className="flex items-center gap-2 justify-center mb-6" data-id="element-137">
                  <h2 className="text-2xl font-semibold text-foreground" data-id="element-138">
                    Come posso aiutarti?
                  </h2>
                </div>

                {/* Search field */}
                <div className="relative" data-id="element-139">
                  <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" data-id="element-140" />
                  <input type="text" autoFocus value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleSubmit(inputValue);
                }
              }} placeholder="Chiedi qualcosa all'AI..." className="w-full pl-14 pr-6 py-4 text-lg border border-border rounded-2xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-shadow hover:shadow-md" data-id="element-141" />
                </div>
              </div>

              {/* Suggestion boxes */}
              <div className="w-full max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-142">
                {suggestions.map((s, i) => {
              const Icon = s.icon;
              return <motion.div key={i} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.35,
                delay: 0.1 + i * 0.07,
                ease: 'easeOut'
              }} onClick={() => handleSubmit(s.title)} className="flex items-start gap-3 p-4 rounded-xl border border-border bg-white hover:border-accent/40 hover:bg-accent/5 transition-all text-left group cursor-pointer" data-id="element-143">
                      <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/15 transition-colors" data-id="element-144">
                        <Icon className="h-4 w-4 text-accent" data-id="element-145" />
                      </div>
                      <div className="min-w-0" data-id="element-146">
                        <div className="text-sm font-medium text-foreground mb-0.5" data-id="element-147">
                          {s.title}
                        </div>
                        <div className="text-xs text-muted-foreground leading-relaxed" data-id="element-148">
                          {s.description}
                        </div>
                      </div>
                    </motion.div>;
            })}
              </div>
            </motion.div> : <motion.div key="chat-phase" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3
        }} className="flex-1 flex flex-col h-full" data-id="element-149">
              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto px-6 py-8" data-id="element-150">
                <div className="max-w-2xl mx-auto space-y-8" data-id="element-151">
                  {messages.map(msg => <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-152">
                      {msg.role === 'ai' && <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mr-4 mt-1" data-id="element-153">
                          <Sparkles className="h-3.5 w-3.5 text-accent" data-id="element-154" />
                        </div>}

                      <div className={`max-w-[85%] ${msg.role === 'user' ? 'bg-accent text-white rounded-2xl rounded-br-md px-5 py-3.5 text-[15px] leading-relaxed shadow-sm' : 'text-foreground text-[15px] leading-relaxed pt-1'}`} data-id="element-155">
                        {msg.role === 'ai' ? <div className="text-muted-foreground" data-id="element-156">
                            {msg.isTyping ? <TypewriterText text={msg.content} onComplete={() => handleAiComplete(msg.id)} data-id="element-157" /> : formatText(msg.content)}
                          </div> : msg.content}
                      </div>
                    </div>)}

                  {isAiTyping && !messages.find(m => m.isTyping) && <div className="flex justify-start" data-id="element-158">
                      <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mr-4 mt-1" data-id="element-159">
                        <Sparkles className="h-3.5 w-3.5 text-accent" data-id="element-160" />
                      </div>
                      <div className="pt-2.5 flex gap-1" data-id="element-161">
                        <motion.div className="w-1.5 h-1.5 bg-accent/40 rounded-full" animate={{
                    opacity: [0.4, 1, 0.4]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: 0
                  }} data-id="element-162" />
                        <motion.div className="w-1.5 h-1.5 bg-accent/40 rounded-full" animate={{
                    opacity: [0.4, 1, 0.4]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: 0.2
                  }} data-id="element-163" />
                        <motion.div className="w-1.5 h-1.5 bg-accent/40 rounded-full" animate={{
                    opacity: [0.4, 1, 0.4]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: 0.4
                  }} data-id="element-164" />
                      </div>
                    </div>}
                  <div ref={messagesEndRef} className="h-4" data-id="element-165" />
                </div>
              </div>

              {/* Chat Input Area */}
              <div className="bg-white border-t border-border p-4 flex-shrink-0" data-id="element-166">
                <div className="max-w-2xl mx-auto relative" data-id="element-167">
                  <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleSubmit(inputValue);
                }
              }} placeholder="Scrivi un messaggio..." className="w-full pl-5 pr-12 py-3.5 text-[15px] border border-border rounded-2xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-shadow" disabled={isAiTyping} data-id="element-168" />
                  <button onClick={() => handleSubmit(inputValue)} disabled={!inputValue.trim() || isAiTyping} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-opacity" data-id="element-169">
                    <ArrowUp className="h-4 w-4" data-id="element-170" />
                  </button>
                </div>
                <div className="text-center mt-2" data-id="element-171">
                  <span className="text-[11px] text-muted-foreground" data-id="element-172">
                    L'AI può commettere errori. Verifica sempre le informazioni
                    importanti.
                  </span>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}
export function TopBar() {
  const [aiModeOpen, setAiModeOpen] = useState(false);
  return <>
      <div className="hidden md:flex h-16 bg-card border-b border-border items-center justify-between px-6 flex-shrink-0" data-id="element-173">
        {/* Search + AI Mode */}
        <div className="flex items-center gap-3" data-id="element-174">
          <div className="relative" data-id="element-175">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" data-id="element-176" />
            <input type="text" placeholder="Cerca articoli, testate..." className="pl-9 pr-4 py-2 text-sm border border-input rounded-full bg-background w-64 focus:outline-none focus:ring-2 focus:ring-ring" data-id="element-177" />
          </div>
          <Button variant="outline" onClick={() => setAiModeOpen(true)} className="rounded-full h-9 px-4 text-sm border-accent text-accent hover:bg-accent/10 hover:text-accent" data-id="element-178">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" data-id="element-179" />
            AI Mode
          </Button>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2" data-id="element-180">
          <Avatar className="h-9 w-9" data-id="element-181">
            <img src="https://cdn.magicpatterns.com/uploads/gMQpnDVF4sUMZFXw5c1pgV/kh4ijivu_400x400.jpg" alt="Marco C." className="w-full h-full object-cover rounded-full" data-id="element-182" />
          </Avatar>
        </div>
      </div>

      {createPortal(<AnimatePresence data-id="element-183">
          {aiModeOpen && <AIModeOverlay onClose={() => setAiModeOpen(false)} data-id="element-184" />}
        </AnimatePresence>, document.body)}
    </>;
}